/**
 * useDeepLink Hook
 * URL hash-based deep linking for term navigation
 */

import { useState, useEffect, useCallback } from "react";
import type { LexiconItem } from "../types";

interface UseDeepLinkReturn {
  currentHash: string | null;
  setHash: (id: string | null) => void;
  getItemFromHash: () => LexiconItem | null;
}

export function useDeepLink(items: LexiconItem[]): UseDeepLinkReturn {
  const [currentHash, setCurrentHash] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const hash = window.location.hash.slice(1);
    return hash || null;
  });

  // Update URL hash
  const setHash = useCallback((id: string | null) => {
    if (typeof window === "undefined") return;

    if (id) {
      window.history.replaceState(null, "", `#${id}`);
      setCurrentHash(id);
    } else {
      window.history.replaceState(null, "", window.location.pathname);
      setCurrentHash(null);
    }
  }, []);

  // Get item from current hash
  const getItemFromHash = useCallback((): LexiconItem | null => {
    if (!currentHash) return null;
    return items.find((item) => item.id === currentHash) || null;
  }, [currentHash, items]);

  // Listen for hash changes (back/forward navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      setCurrentHash(hash || null);
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  return {
    currentHash,
    setHash,
    getItemFromHash,
  };
}

export default useDeepLink;
